<template>
  <section>
    <HeadingSmall>
      {{ $options.title }}
    </HeadingSmall>
    <ul class="jc-clearlist">
      <li
        :class="$style.option"
      >
        <Checkbox
          v-model="enableMaxHistory"
          :class="$style.checkbox"
          data-test-id="enableHistoryCheckbox"
        />
        <InputNumberInline
          v-model="maxHistory"
          data-test-id="enableHistoryInput"
          :isDisabled="!enableMaxHistory"
          name="maximum history"
          :required="enableMaxHistory"
          rules="between:1,24|integer"
          :textAfter="$options.labels.maxHistory"
        />
      </li>
      <li
        :class="$style.option"
      >
        <Checkbox
          v-model="enablePasswordExpirationInDays"
          :class="$style.checkbox"
          data-test-id="passwordExpirationCheckbox"
        />
        <InputNumberInline
          v-model="passwordExpirationInDays"
          data-test-id="passwordExpirationInput"
          :isDisabled="!enablePasswordExpirationInDays"
          name="password expiration"
          :required="enablePasswordExpirationInDays"
          rules="integer|max:3"
          :textAfter="$options.labels.passwordExpirationInDays"
        />
      </li>
      <li
        :class="[$style.option, $style.suboption]"
      >
        <Checkbox
          v-model="enableDaysBeforeExpirationToForceReset"
          :class="$style.checkbox"
          data-test-id="passwordDaysBeforeExpirationCheckbox"
          :isDisabled="!enablePasswordExpirationInDays"
        />
        <InputNumberInline
          v-model="daysBeforeExpirationToForceReset"
          data-test-id="passwordDaysBeforeExpirationInput"
          :isDisabled="!enableDaysBeforeExpirationToForceReset"
          name="days before expiration"
          :required="enableDaysBeforeExpirationToForceReset"
          rules="integer|max:3"
          :textAfter="$options.labels.daysBeforeExpirationToForceReset"
        />
      </li>
      <li
        :class="$style.option"
      >
        <Checkbox
          v-model="enableDaysAfterExpirationToSelfRecover"
          data-test-id="enableExpirationSelfRecoverCheckbox"
          :label="$options.labels.enableDaysAfterExpirationToSelfRecover"
        />
      </li>
    </ul>
    <ModalConfirmChange
      data-test-id="passwordAgingConfirmChange"
      :itemChanged="activeModal"
      :modalText="modalText"
      :modalType="modalType"
      :showModal="!!activeModal"
      @cancel="handleCancelModal"
      @save="handleContinueModal"
    />
  </section>
</template>

<script>
import {
  Checkbox, HeadingSmall, InputNumberInline,
} from '@jumpcloud/ui-components';
import { SecurityModalTypes } from 'client/settings/Security/SecuritySettingsConstants';
import ModalConfirmChange from 'client/settings/Security/ModalConfirmChange.vue';
import createComputedObjectBinding from 'client/util/createComputedObjectBinding';

export const defaultValues = {
  maxHistory: 3,
  passwordExpirationInDays: 90,
  daysBeforeExpirationToForceReset: 10,
};
const labels = {
  maxHistory: 'most recent passwords cannot match each other (limit historical reuse)',
  passwordExpirationInDays: 'days until password expiration',
  daysBeforeExpirationToForceReset: 'days prior to password expiration, require password reset at login',
  enableDaysAfterExpirationToSelfRecover: 'Allow password change after expiration ',
};
const title = 'Password Aging';

export default {
  name: 'FieldsPasswordAging',

  components: {
    Checkbox,
    HeadingSmall,
    InputNumberInline,
    ModalConfirmChange,
  },

  props: {
    value: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      activeModal: '',
      modalText: [],
      modalType: '',
    };
  },

  computed: {
    daysBeforeExpirationToForceReset: {
      ...createComputedObjectBinding('daysBeforeExpirationToForceReset'),
    },

    enableDaysAfterExpirationToSelfRecover: {
      get() {
        return this.value.enableDaysAfterExpirationToSelfRecover;
      },
      set(newValue) {
        const item = 'enableDaysAfterExpirationToSelfRecover';

        this.$emit('input', {
          ...this.value,
          [item]: newValue,
        });

        this.activeModal = item;
        this.modalType = SecurityModalTypes.passwordAging;

        if (newValue === true) {
          this.modalText = [
            'Are you sure you want to enable allow password change after expiration? Users that are currently in an expired state will be able to update their passwords to access their accounts.',
          ];
        } else {
          this.modalText = [
            "Users won't be able to self-recover their accounts if their password expires and you'll have to help them regain access.",
            "Your custom grace period setting will be turned off permanently and won't come back if you enable it again.",
          ];
        }
      },
    },

    enableDaysBeforeExpirationToForceReset: {
      get() {
        return this.value.enableDaysBeforeExpirationToForceReset;
      },

      set(newValue) {
        const isDisabledEmpty = newValue === false
        && this.value.daysBeforeExpirationToForceReset === '';
        const daysBeforeExpirationToForceReset = isDisabledEmpty
          ? defaultValues.daysBeforeExpirationToForceReset
          : this.value.daysBeforeExpirationToForceReset;

        this.$emit('input', {
          ...this.value,
          enableDaysBeforeExpirationToForceReset: newValue,
          daysBeforeExpirationToForceReset,
        });
      },
    },

    enableMaxHistory: {
      get() {
        return this.value.enableMaxHistory;
      },

      set(newValue) {
        const isDisabledEmpty = newValue === false && this.value.maxHistory === '';
        const maxHistory = isDisabledEmpty
          ? defaultValues.maxHistory
          : this.value.maxHistory;

        this.$emit('input', {
          ...this.value,
          enableMaxHistory: newValue,
          maxHistory,
        });
      },
    },

    enablePasswordExpirationInDays: {
      get() {
        return this.value.enablePasswordExpirationInDays;
      },
      set(newValue) {
        const item = 'enablePasswordExpirationInDays';
        const isDisabledEmpty = newValue === false && this.value.passwordExpirationInDays === '';
        const passwordExpirationInDays = isDisabledEmpty
          ? defaultValues.passwordExpirationInDays
          : this.value.passwordExpirationInDays;
        const enableDaysBeforeExpirationToForceReset = newValue
          ? this.value.enableDaysBeforeExpirationToForceReset
          : false;

        this.$emit('input', {
          ...this.value,
          [item]: newValue,
          enableDaysBeforeExpirationToForceReset,
          passwordExpirationInDays,
        });

        if (newValue === true) {
          this.activeModal = item;

          this.modalText = ['Your users will receive one email per day for 7 days leading up to their periodic password expiration date requesting them to change their password.'];
          this.modalType = SecurityModalTypes.passwordAging;
        }
      },
    },

    maxHistory: {
      ...createComputedObjectBinding('maxHistory'),
    },

    passwordExpirationInDays: {
      ...createComputedObjectBinding('passwordExpirationInDays'),
    },
  },

  labels,
  title,

  methods: {
    handleCancelModal(itemChanged) {
      this.activeModal = '';
      this.$emit('input', {
        ...this.value,
        [itemChanged]: !this.value[itemChanged],
      });
    },

    handleContinueModal(itemChanged) {
      this.activeModal = '';
      this.$emit('input', {
        ...this.value,
        [itemChanged]: this.value[itemChanged],
      });
    },
  },
};
</script>

<style module scoped>
@import '../settings.css';
</style>
